import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowRightIcon, CheckCircleIcon, ChevronRightIcon, MagnifyingGlassIcon, CalculatorIcon,
  LockClosedIcon, DocumentArrowUpIcon,
} from '@heroicons/react/24/outline'
import { fetchProducts } from '../../requests'
import KalkulatorMocy from './KalkulatorMocy'
import GrafikaKompatybilnosc from './GrafikaKompatybilnosc'
import GrafikaFalownikLadowarkaAuto from './GrafikaFalownikLadowarkaAuto'
import GrafikaOferta from './GrafikaOferta'
import WyborProduktow from './WyborProduktow'
import { useTranslation } from 'react-i18next'
import { KRAJ, jestPolska } from '../../kraj'
import { RYNEK_WLACZONY } from '../../config/rynek'
import { formatPrice } from '../../utils'

/**
 * Hero strony głównej = REALNY POCZĄTEK ASYSTENTA, a nie kafelek z przyciskiem.
 *
 * Asystent doboru jest główną funkcją serwisu, a oferty i modele są fundamentem
 * pod niego — dlatego zajmuje centralne miejsce, a przeglądanie kategorii jest
 * ścieżką poboczną po prawej stronie.
 */

/**
 * Co dobieramy: karty ze zdjęciami produktów (WyborProduktow.jsx).
 *
 * ═══ HISTORIA, ŻEBY NIE ZATOCZYĆ KOŁA ═══
 * Najpierw był wybór modułów, falownika i magazynu osobno. Zastąpiły go trzy
 * warianty („bez magazynu / z magazynem / zastanawiam się”), bo lista komponentów
 * wymagała wiedzy fachowca, zanim ktoś w ogóle zaczął. Od 2026-09-30 (prośba
 * właściciela) wybór znów jest per element, ale na KARTACH ZE ZDJĘCIAMI (rysunek
 * domu do klikania odrzucony tego samego dnia jako „jak z Painta”): zdjęcie mówi,
 * co to za urządzenie, więc nie trzeba wiedzieć, co to falownik. Pierwotny problem łagodzi
 * też domyślny stan: zaznaczona cała instalacja z magazynem (jak dawny domyślny
 * wariant), więc laik po prostu klika „Dobierz mój zestaw”, a kto wie, czego
 * chce, odznacza zbędne.
 *
 * „Zastanawiam się” zniknęło razem z przyciskami. Otwierało kalkulator mocy
 * samo; kalkulator został pod przyciskiem „Dobierzemy moc”, a asystent i tak
 * zatrzymuje się na etapie mocy, gdy przy modułach jej brakuje.
 */
const KOLEJNOSC_KATEGORII = ['panels', 'inverter', 'battery']
const WYBOR_DOMYSLNY = { panels: true, inverter: true, battery: true }

// `dobor` to wejście do asystenta TEJ kategorii. Cztery asystenty komponentów
// nie miały na stronie głównej żadnego śladu — dało się do nich trafić wyłącznie
// z listingu albo z menu w pasku, czyli już po wybraniu drogi „przeglądam sam".
// `licznik` wskazuje, którą liczbę modeli z bazy pokazać w rogu kafelka; jest też
// kluczem nazwy kategorii w słowniku `glowna` (hero.kategorie.<licznik>).
const KATEGORIE_PRZEGLADANIA = [
  { img: '/panels.png', to: '/panele', dobor: '/dobor-modulow', licznik: 'panele' },
  { img: '/inverters.png', to: '/inwertery', dobor: '/dobor-falownika', licznik: 'falowniki' },
  { img: '/magazines.png', to: '/magazyny', dobor: '/dobor-magazynu', licznik: 'magazyny' },
  { img: '/ev_chargers.png', to: '/ladowarki', dobor: '/dobor-ladowarki', licznik: 'ladowarki' },
].filter((k) => jestPolska || k.licznik !== 'ladowarki')   // ładowarki tylko w Polsce (brak ofert RO)

/**
 * Kategorie wyszukiwarki — muszą pokrywać się z trasami listingów.
 *
 * `podpowiedz` to przykład Z TEJ kategorii, a nie ogólny. Wcześniej we wszystkich
 * trzech stało „np. Deye 10kW", czyli falownik — także wtedy, gdy szukało się
 * modułów. Każda podpowiedź jest sprawdzona: wpisana dosłownie zwraca wynik,
 * bo przykład, który niczego nie znajduje, jest gorszy niż jego brak.
 * Etykiety i podpowiedzi są w słowniku `glowna` (hero.szukanie.<key>).
 * Podpowiedź do ładowarek sprawdzona jak pozostałe: "Zaptec Go" wpisane dosłownie
 * zwraca 8 ofert (katalog polski).
 */
const KATEGORIE_SZUKANIA = [
  { key: 'panele', route: '/panele' },
  { key: 'inwertery', route: '/inwertery' },
  { key: 'magazyny', route: '/magazyny' },
  { key: 'ladowarki', route: '/ladowarki' },
].filter((k) => jestPolska || k.key !== 'ladowarki')

const BUDZET_MIN = 10000
const BUDZET_MAX = 100000
const BUDZET_KROK = 1000

/**
 * Kroki i zobowiązania WSPÓLNE dla obu dróg z hero: doboru zestawu od zera
 * i sprawdzenia gotowego zestawu albo oferty od instalatora (decyzja
 * właściciela 2026-09-30). Wcześniej opisywały sam asystent, więc obok kafelka
 * oferty czytały się jak instrukcja do czegoś innego.
 *
 * ═══ CZEGO TU ŚWIADOMIE NIE MA ═══
 * Krok „Wybierasz sprawdzonego wykonawcę” i fajka „Tylko sprawdzeni wykonawcy”
 * odpadły: katalog wykonawców jest pusty, a „Zapytaj o wycenę” wyłączone
 * (second-brain/07, znany problem 1). Wrócą, gdy katalog zostanie opublikowany.
 * „Gwarancja najniższej ceny” odpadła, bo dotyczyła tylko doboru, a przy
 * sprawdzaniu oferty nic nie gwarantujemy, tylko pokazujemy typowy zakres.
 *
 * Każde zdanie musi być prawdziwe dla OBU dróg. „Ok. 30 sklepów” za
 * second-brain/01; „nie sprzedajemy” za modelem przychodów w tym samym pliku
 * (projekt jest przed monetyzacją, planowane źródła to abonamenty i partnerstwa).
 *
 * Teksty w słowniku `glowna` (hero.kroki.<id>). Wersja rumuńska nie wspomina
 * o sprawdzaniu oferty ani o liczbie sklepów: kafelek oferty jest tam ukryty,
 * a „ok. 30” dotyczy katalogu polskiego.
 */
const KROKI = ['potrzeby', 'ceny', 'sprzet', 'wybor']

// Krótkie, bo muszą zmieścić się w jednym rzędzie: przy 1440 px rząd ma 625 px,
// a z „Nie sprzedajemy sprzętu ani montażu” trzy pozycje zajmowały 645 px i ostatnia
// spadała do drugiej linii. „Instalacji” mówi to samo krócej: nie jesteśmy ani
// sklepem, ani firmą montażową.
const ZOBOWIAZANIA = ['zgodnosc', 'nie_sprzedajemy', 'za_darmo']

// Kwota w walucie kraju. Zwykła spacja przed walutą (nie twarda jak w formatKwota),
// żeby polska wersja wyglądała dokładnie jak przed wyjęciem tekstów.
const zl = (v) => `${formatPrice(v)} ${KRAJ.symbol}`

// 322 -> „300+”. Deklaracja pozostaje prawdziwa, a nie skacze przy imporcie.
// Poniżej setki schodzimy do dziesiątek (ładowarek jest mniej niż pozostałych
// kategorii), a poniżej dziesięciu nie pokazujemy nic: „0+” nie jest informacją.
const doSetek = (n) => {
  if (n >= 100) return `${Math.floor(n / 100) * 100}+`
  if (n >= 10) return `${Math.floor(n / 10) * 10}+`
  return null
}

const HeroAssistant = () => {
  const { t } = useTranslation('glowna')
  const navigate = useNavigate()
  const [wybrane, setWybrane] = useState(WYBOR_DOMYSLNY)
  const [moc, setMoc] = useState('')
  const [budzet, setBudzet] = useState(45000)
  const [liczby, setLiczby] = useState(null)
  const [fraza, setFraza] = useState('')
  const [kategoriaSzukania, setKategoriaSzukania] = useState('panele')
  const [kalkulatorOtwarty, setKalkulatorOtwarty] = useState(false)
  // Pojemność policzona w kalkulatorze jedzie dalej do asystenta, żeby nie trzeba
  // jej było wpisywać drugi raz. Pusta, gdy ktoś nie liczył albo wybrał bez magazynu.
  const [pojemnosc, setPojemnosc] = useState('')

  const przelacz = (kategoria) => setWybrane((w) => ({ ...w, [kategoria]: !w[kategoria] }))
  const kategorie = KOLEJNOSC_KATEGORII.filter((k) => wybrane[k])

  // Realne liczby z bazy zamiast okrągłych deklaracji. Gdy API nie odpowie,
  // sekcja po prostu się nie pojawi — lepiej nic niż wymyślona liczba.
  useEffect(() => {
    let anulowane = false
    Promise.all(
      ['panel_models', 'inverter_models', 'magazine_models', 'wallbox_models'].map((ep) =>
        fetchProducts(`${ep}/?limit=1`).then((d) => (d && d.count) || 0).catch(() => 0)
      )
    ).then(([p, i, m, w]) => {
      if (!anulowane && p + i + m > 0) setLiczby({ panele: p, falowniki: i, magazyny: m, ladowarki: w })
    })
    return () => { anulowane = true }
  }, [])

  // Pozycja uchwytu w procentach — używana i przez ślad suwaka, i przez dymek z kwotą.
  const procentBudzetu = ((budzet - BUDZET_MIN) / (BUDZET_MAX - BUDZET_MIN)) * 100

  const start = () => {
    if (!kategorie.length) return
    // Cała instalacja bez podanej mocy (2026-10-23, makieta „VoltValue dla domu”, D3): tryb prosty z pytaniami
    // o dom i rachunki, a nie pełny asystent, który zatrzymuje się na polu kWp. Magazyn i budżet z tego formularza
    // jadą dalej, żeby nie pytać o nie drugi raz; budżet w obu trybach znaczy całość z montażem.
    if (!moc && wybrane.panels && wybrane.inverter) {
      const p = new URLSearchParams()
      p.set('magazyn', wybrane.battery ? 'tak' : 'nie')
      p.set('budzet', String(budzet))
      navigate(`/dobierz-zestaw?${p.toString()}`)
      return
    }
    const q = new URLSearchParams()
    q.set('czego', kategorie.join(','))
    if (moc) q.set('kwp', moc)
    if (pojemnosc && wybrane.battery) q.set('kwh', String(pojemnosc))
    q.set('budzet', String(budzet))
    navigate(`/dobor-zestawu-pv?${q.toString()}`)
  }

  const szukaj = () => {
    const q = fraza.trim()
    const kat = KATEGORIE_SZUKANIA.find((k) => k.key === kategoriaSzukania) || KATEGORIE_SZUKANIA[0]
    navigate(q ? `${kat.route}?search=${encodeURIComponent(q)}&page=1` : kat.route)
  }

  return (
    <section className="px-4 md:px-8 lg:px-12 pt-6">
      {/* Panel na PEŁNĄ szerokość ekranu (bez `max-w` i centrowania): logo i pytanie
          startowe zaczynają się przy lewej krawędzi, a cała odzyskana przestrzeń
          idzie na prawą kolumnę, kafelki kategorii mogą być szerokie. */}
      <div className="rounded-3xl bg-gradient-to-br from-blue-50 via-white to-white
                      border border-blue-100/70 overflow-hidden">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 p-6 sm:p-8">

          {/* ══ Kolumna główna: asystent ══ */}
          <div className="min-w-0">

            {/* Logo obok nagłówka. Na stronie głównej znika z nawigacji,
                żeby marka nie pojawiała się dwa razy na jednym ekranie. */}
            <div className="flex items-center gap-5 mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/navLogo.png`}
                alt="VoltValue"
                className="h-20 sm:h-28 w-auto flex-shrink-0"
              />
              <div className="min-w-0">
                <span className="inline-flex items-center rounded-full bg-blue-600 text-white
                                 text-[11px] font-semibold px-2.5 py-1 uppercase tracking-wide">
                  {t('hero.odznaka')}
                </span>
                <h1 className="mt-2.5 text-2xl sm:text-3xl font-bold text-gray-900 leading-[1.2]">
                  {t('hero.naglowek_1')}<br />
                  <span className="text-blue-600">{t('hero.naglowek_2')}</span>
                </h1>
              </div>
            </div>

            <p className="text-[15px] text-gray-600 leading-7 mb-5">
              {t('hero.wstep')}
            </p>

            {/* Wejście na „VoltValue dla domu” (2026-10-24, rekomendacja D-a): zlecenie na Rynku, wykonawcę wybiera
                klient. Tylko w Polsce, bo Rynku nie ma w innych krajach. */}
            {jestPolska && RYNEK_WLACZONY && (
              <Link to="/dla-domu"
                    className="mb-5 inline-flex max-w-full items-center gap-2.5 rounded-2xl border border-gray-200 bg-white py-1.5 pl-1.5 pr-3.5
                               text-left text-sm font-medium text-gray-700 no-underline shadow-sm transition hover:border-blue-300 hover:text-gray-900">
                <span className="flex-shrink-0 rounded-full bg-blue-600 px-2.5 py-0.5 text-xs font-semibold text-white">{t('hero.dla_domu.etykieta')}</span>
                <span className="min-w-0">{t('hero.dla_domu.tekst')}</span>
                <ArrowRightIcon className="h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
              </Link>
            )}

            {/* Poza Polską kafelka „Oferta pod lupą” nie ma (reguły sprawdzania oferty
                są pod polskie dotacje i VAT, jak w Navbar.jsx), więc formularz stoi sam. */}
            <div className={`grid gap-6 ${jestPolska ? 'md:grid-cols-2' : 'max-w-xl'}`}>

              {/* ── Formularz startowy ── */}
              <div className="vv-kafel p-4">
                {/* Nagłówek tej samej wielkości co w kafelku oferty obok, z krótką
                    nazwą drogi nad nim: dwa kafelki mają czytać się jak para
                    równorzędnych wyborów, a mały napis „Czego potrzebujesz?” przy
                    dużym pytaniu obok wyglądał na element drugiego rzędu. */}
                <p className="text-sm font-semibold text-blue-600">{t('hero.formularz.nazwa')}</p>
                <h2 className="mt-0.5 text-lg font-bold text-gray-900 leading-snug">{t('hero.formularz.pytanie')}</h2>
                <p className="mt-0.5 text-xs text-gray-500">{t('hero.formularz.zaznacz')}</p>

                {/* Karty ze zdjęciami zamiast trzech przycisków wariantów (patrz
                    WyborProduktow.jsx i komentarz przy KOLEJNOSC_KATEGORII). */}
                <div className="mt-2 mb-2.5">
                  <WyborProduktow wybrane={wybrane} onPrzelacz={przelacz} />
                  {!kategorie.length && (
                    <p className="mt-1 text-xs text-amber-600 text-center">
                      {t('hero.formularz.zaznacz_jedna')}
                    </p>
                  )}
                </div>

                <div className="mb-3">
                  <div className="flex items-baseline justify-between gap-2 mb-1.5">
                    {/* Bez dopisku „opcjonalnie": pole i tak stoi obok przycisku
                        „Dobierzemy moc", więc alternatywa jest widoczna sama z siebie. */}
                    <p className="text-sm font-semibold text-gray-800">{t('hero.formularz.moc')}</p>
                  </div>
                  {/* Dwie RÓWNE połówki zamiast „wąskie pole + reszta dla przycisku".
                      Pole miało sztywne 96 px, a przycisk zabierał całą pozostałą
                      szerokość, więc wyglądały jak element główny i doklejka.
                      Flex, a nie grid: między połówki wchodzi jeszcze „lub",
                      które ma zajmować tylko tyle, ile zajmuje słowo. */}
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1 basis-0 min-w-0">
                      <input
                        type="number" min="1" max="100" step="0.5" value={moc}
                        onChange={(e) => { setMoc(e.target.value); setPojemnosc('') }}
                        placeholder={t('hero.formularz.moc_placeholder')}
                        aria-label={t('hero.formularz.moc_aria')}
                        className="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-200 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">kWp</span>
                    </div>
                    {/* „lub" mówi wprost, że to dwie drogi do tego samego pola,
                        a nie pole i osobne narzędzie obok. */}
                    <span className="flex-shrink-0 text-xs text-gray-400 select-none">{t('hero.formularz.lub')}</span>
                    {/* „Nie wiesz?” przypięte do przycisku (`absolute`, nad jego lewą
                        krawędzią), a nie w osobnym wierszu: pole i przycisk nie są
                        równymi połówkami, bo napis przycisku się nie łamie, więc
                        osobny wiersz rozjeżdżał się o kilka pikseli. Zachęca tego,
                        kto waha się przy mocy, żeby dał ją policzyć (prośba
                        właściciela 2026-09-30). */}
                    <div className="relative flex-1 basis-0 min-w-0 self-stretch flex">
                    <span className="absolute bottom-full left-0 mb-1.5 text-xs font-semibold text-blue-600 whitespace-nowrap">
                      {t('hero.formularz.nie_wiesz')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setKalkulatorOtwarty(true)}
                      // `whitespace-nowrap` + ciasniejsze odstepy: przy rownych
                      // polowkach etykieta lamala sie na dwie linie i przycisk byl
                      // o 10 px wyzszy od pola obok.
                      // Drugorzędny (jasne tło, niebieski napis), a nie pełny niebieski:
                      // dwa pełne niebieskie przyciski w jednym kafelku konkurowały
                      // o uwagę, a głównym wezwaniem jest „Dobierz mój zestaw”.
                      className="flex-1 min-w-0 flex items-center justify-center gap-1 px-2 py-2 rounded-lg
                                 border border-blue-200 bg-blue-50 text-blue-700 text-xs font-semibold whitespace-nowrap
                                 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                    >
                      <CalculatorIcon className="w-4 h-4 flex-shrink-0" />
                      {t('hero.formularz.dobierzemy_moc')}
                    </button>
                    </div>
                  </div>
                  {/* Przewymiarowanie to najczęstsza wada kalkulatorów mocy, mówimy
                      wprost, że tego nie robimy, zanim ktoś zdąży założyć, że robimy. */}
                  <p className="mt-1 text-[11px] leading-4 text-gray-500">
                    {t('hero.formularz.bez_przewymiarowania')}
                  </p>
                </div>

                {/* Przycisk NAD budżetem: budżet jest doprecyzowaniem, a nie
                    warunkiem startu (suwak ma sensowną wartość domyślną), więc
                    wezwanie do działania nie ma czekać na jego koniec. */}
                {/* `py-2.5`: ta sama wysokość (44 px) co „Sprawdź swoją ofertę”
                    w kafelku obok; szerokość wychodzi równa sama z siebie. */}
                <button
                  type="button"
                  onClick={start}
                  disabled={!kategorie.length}
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl
                             bg-blue-600 text-white font-semibold hover:bg-blue-500 active:bg-blue-700
                             transition-colors shadow-sm
                             disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none
                             disabled:cursor-not-allowed"
                >
                  {t('hero.formularz.start')}
                  <ArrowRightIcon className="w-5 h-5" />
                </button>


                {/* `mb-0.5`, a nie `mb-2`: dymek z kwotą stał za daleko od napisu
                    (uwaga właściciela 2026-09-30). */}
                <p className="mt-3 text-sm font-semibold text-gray-800 mb-0.5">{t('hero.formularz.budzet')}</p>
                <div className="relative pt-8">
                  {/* BEZ animacji `left`: przejście między wartościami calc()
                      z procentami potrafi zablokować wyliczanie pozycji i dymek
                      zostaje w miejscu mimo poprawnego stylu. Zmierzone. */}
                  <span
                    className="absolute top-0 -translate-x-1/2 px-2.5 py-1 rounded-lg bg-blue-600
                               text-white text-[13px] font-bold whitespace-nowrap shadow-md"
                    style={{ left: `calc(${procentBudzetu}% + ${13 - 0.26 * procentBudzetu}px)` }}
                  >
                    {zl(budzet)}
                    <span className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 -mt-1
                                     rotate-45 bg-blue-600" />
                  </span>

                  <input
                    type="range"
                    min={BUDZET_MIN} max={BUDZET_MAX} step={BUDZET_KROK}
                    value={budzet}
                    onChange={(e) => setBudzet(Number(e.target.value))}
                    aria-label={t('hero.formularz.budzet_aria')}
                    className="vv-range"
                    style={{
                      // Niewypełniona część śladu bierze kolor z motywu — na sztywno
                      // wpisana jasna szarość świeciłaby na ciemnym tle.
                      background: `linear-gradient(to right,
                        #1d4ed8 0%, #3b82f6 ${procentBudzetu}%,
                        rgb(var(--vv-bg-gray-200)) ${procentBudzetu}%, rgb(var(--vv-bg-gray-200)) 100%)`,
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[11px] text-gray-400">{zl(BUDZET_MIN)}</span>
                  <span className="text-[11px] text-gray-400">{zl(BUDZET_MAX)}+</span>
                </div>
              </div>

              {/* ── Oferta pod lupą ──
                  Pełny kafelek obok formularza, bo to druga z dwóch głównych
                  dróg: asystent jest dla kogoś, kto dopiero wybiera sprzęt, a ten
                  kafelek dla kogoś, kto ma już wycenę od instalatora.

                  CAŁY kafelek jest jednym odnośnikiem z jednym przyciskiem: dwa
                  przyciski „Wgraj” i „Uzupełnij ręcznie” prowadziły w to samo
                  miejsce, a wyglądały jak dwa różne wybory.

                  Białe tło jak formularz obok i niebieska FALA na dole zamiast
                  całego kafelka w mocnym niebieskim (uwaga właściciela 2026-09-30):
                  para kafelków wygląda spójnie, a kolor marki niesie jeden element,
                  na którym leży przycisk. Ta sama klasa `vv-kafel` co formularz,
                  więc ramka, cień i uniesienie pod kursorem są identyczne.

                  Tylko w Polsce: Navbar też chowa „Sprawdź ofertę” poza Polską. */}
              {jestPolska && (
              <Link
                to="/sprawdz-oferte"
                className="vv-kafel group relative overflow-hidden flex flex-col
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                           focus-visible:ring-offset-2"
              >
                <div className="relative px-4 pt-4">
                  <p className="text-sm font-semibold text-blue-600">{t('hero.oferta.nazwa')}</p>
                  <h2 className="mt-0.5 text-lg font-bold text-gray-900 leading-snug">
                    {t('hero.oferta.pytanie')}
                  </h2>
                  <p className="mt-1.5 text-[13px] leading-5 text-gray-600">
                    {t('hero.oferta.opis')}
                  </p>
                </div>

                {/* Rysunek wypełnia wolną wysokość kafelka (`flex-1`), bo kafelek
                    jest tak wysoki jak formularz obok. `z-10`: dolna krawędź kartki
                    nachodzi na falę, jakby na niej leżała. Lekko przekrzywiona
                    kartka prostuje się pod kursorem. */}
                {/* 250 px: było 312 (+25%, 2026-09-30), ale 2026-10-01 właściciel
                    zmniejszył cały kafelek o ok. 20%, żeby kroki 1 do 4 były widoczne
                    bez przewijania. Pionowa kartka na cały kafelek była za duża. */}
                <div className="relative z-10 flex-1 flex items-center justify-center px-3 pt-2">
                  <GrafikaOferta
                    className="w-full max-w-[250px] -rotate-2 transition-transform duration-300
                               group-hover:rotate-0 drop-shadow-[0_14px_22px_rgba(15,23,42,0.22)]"
                  />
                </div>

                {/* ── Fala ──
                    Krzywa w SVG tylko na górnej krawędzi (stała wysokość, szerokość
                    rozciągana), a pod nią zwykły blok z gradientem. Przy
                    rozciąganiu całego SVG na wysokość kafelka amplituda fali
                    zmieniałaby się razem z wysokością formularza obok.
                    Tylna fala półprzezroczysta, żeby w ciemnym motywie nie świeciła
                    jasnym pasem na granatowym tle. */}
                <div className="relative -mt-10">
                  <svg viewBox="0 0 320 56" preserveAspectRatio="none" aria-hidden="true"
                       className="block w-full h-12">
                    <path d="M0 22C70 4 150 40 230 18C270 8 300 10 320 16V56H0Z"
                          fill="#3b82f6" fillOpacity="0.28" />
                    <path d="M0 38C80 18 170 54 250 30C285 20 305 22 320 26V56H0Z" fill="#2563eb" />
                  </svg>
                  <div className="bg-gradient-to-b from-[#2563eb] to-[#1d4ed8] px-4 pb-4 pt-0.5">
                    {/* Kolory przycisku wpisane na sztywno: `bg-white` i `text-blue-700`
                        są w ciemnym motywie podmieniane, a fala zostaje niebieska,
                        więc przycisk musi zostać biały z niebieskim napisem.
                        Ikona wgrywania po lewej mówi, że można od razu wrzucić plik;
                        strzałka po prawej, że przycisk prowadzi dalej. */}
                    <span className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl
                                     bg-[#ffffff] text-[#1d4ed8] font-semibold shadow-sm transition-colors
                                     group-hover:bg-[#eff6ff]">
                      <DocumentArrowUpIcon className="w-5 h-5 flex-shrink-0" />
                      {t('hero.oferta.przycisk')}
                      <ArrowRightIcon className="w-5 h-5 flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
                    </span>
                    <p className="mt-2 text-center text-xs leading-4 text-[#dbeafe] text-balance">
                      {t('hero.oferta.pliki')}
                    </p>
                    <p className="mt-1 flex items-center justify-center gap-1.5 text-[11px] leading-4 text-[#bfdbfe]">
                      <LockClosedIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      {t('hero.oferta.prywatnosc')}
                    </p>
                  </div>
                </div>
              </Link>
              )}
            </div>

            {/* ── Jak to działa: wspólne dla obu dróg ──
                Jedna oprawiona karta zamiast luźnych kroków i fajek pod spodem:
                bez ramki cztery krótkie teksty rozchodziły się po szerokości
                i wyglądały na rozstrzelone. Kreska od numeru do następnego kroku
                łączy je w jeden proces. Stoi pod OBOMA kafelkami, bo opisuje obie
                drogi naraz. */}
            <div className="mt-6 rounded-2xl border border-blue-100 bg-white/70 px-5 py-4">
              <ol className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-5">
                {/* `grid-rows-subgrid` + `row-span-3`: numer, tytuł i opis każdego
                    kroku leżą we WSPÓLNYCH wierszach siatki. Gdy na węższym ekranie
                    jeden tytuł złamie się na dwie linie, opisy pozostałych kroków
                    zejdą razem z nim i dalej trzymają jedną linię. */}
                {KROKI.map((id, i) => (
                  <li key={id} className="min-w-0 grid grid-rows-subgrid row-span-3 gap-y-0">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-7 h-7 rounded-full
                                       bg-blue-600 text-white text-xs font-bold flex-shrink-0 shadow-sm">
                        {i + 1}
                      </span>
                      {i < KROKI.length - 1 && (
                        <span aria-hidden="true" className="hidden sm:block h-px flex-1 bg-blue-100" />
                      )}
                    </div>
                    <p className="mt-2.5 text-sm font-semibold text-gray-900 leading-5">{t(`hero.kroki.${id}.tytul`)}</p>
                    <p className="mt-0.5 text-xs text-gray-500 leading-5">{t(`hero.kroki.${id}.opis`)}</p>
                  </li>
                ))}
              </ol>

              <ul className="mt-4 pt-3.5 border-t border-blue-100 flex flex-wrap gap-x-6 gap-y-1.5">
                {ZOBOWIAZANIA.map((id) => (
                  <li key={id} className="flex items-start gap-2 text-xs text-gray-600 leading-5 whitespace-nowrap">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    {t(`hero.zobowiazania.${id}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ══ Kolumna boczna: przeglądanie na własną rękę ══
              Ścieżka dla osób, które wiedzą, czego szukają, i nie potrzebują
              doboru, świadomie węższa i wizualnie lżejsza od asystenta. */}
          <aside className="min-w-0">
            {/* Nagłówek w WŁASNEJ ramce, nie gołym tekstem nad wyszukiwarką:
                to druga z dwóch równorzędnych dróg na tej stronie (asystent albo
                wybór samodzielny), a bez oprawy wyglądała jak podpis do pola
                szukania. Delikatne tło, nie kolejna biała karta — inaczej zlałby
                się z kartą wyszukiwarki tuż pod spodem. */}
            {/* ── Nagłówek RAZEM z wyszukiwarką ──
                Jedna karta zamiast dwóch: nagłówek zapowiada dokładnie to, co
                jest pod nim (znajdź model albo przeglądaj kategorię), więc
                rozdzielony ramką czytał się jak osobna sekcja bez treści.
                Wyszukiwarka wróciła tu razem z wyborem kategorii: bez niego nie
                wiadomo, którą listę przeszukać, a „Deye 10kW” w modułach nic
                nie zwróci. */}
            <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100
                            shadow-sm p-5 mb-4">
              {/* Lupa jest OZDOBĄ, nie ikoną do kliknięcia: rysowana w SVG, a nie
                  wstawiona plikiem, bo skaluje się bez rozmycia na każdym ekranie
                  i bierze kolor z motywu. `aria-hidden`, żeby czytnik ekranu jej
                  nie ogłaszał — nie niesie żadnej treści. */}
              <svg viewBox="0 0 100 100" aria-hidden="true"
                   className="pointer-events-none absolute -right-4 -top-3 w-28 h-28 text-blue-500/25">
                <circle cx="42" cy="42" r="26" fill="none" stroke="currentColor" strokeWidth="6" />
                <line x1="61" y1="61" x2="84" y2="84" stroke="currentColor"
                      strokeWidth="8" strokeLinecap="round" />
              </svg>

              <div className="relative pr-20">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
                  {t('hero.samodzielnie.tytul')}
                </h2>
                <p className="mt-1.5 text-sm text-gray-600 leading-6">
                  {t('hero.samodzielnie.opis')}
                </p>
                <p className="mt-2 mb-4 flex items-start gap-2 text-sm text-gray-700 leading-6">
                  <CheckCircleIcon className="w-[18px] h-[18px] text-blue-600 flex-shrink-0 mt-1" />
                  <span>
                    {t('hero.samodzielnie.zawsze')}{' '}
                    <strong className="font-semibold text-blue-700">{t('hero.samodzielnie.aktualne_oferty')}</strong>{' '}
                    {t('hero.samodzielnie.z_wielu')}
                  </span>
                </p>
              </div>

              <div className="relative flex gap-1.5 flex-wrap mb-3">
                {KATEGORIE_SZUKANIA.map((kat) => (
                  <button
                    key={kat.key}
                    type="button"
                    onClick={() => setKategoriaSzukania(kat.key)}
                    aria-pressed={kategoriaSzukania === kat.key}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      kategoriaSzukania === kat.key
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {t(`hero.szukanie.${kat.key}.etykieta`)}
                  </button>
                ))}
              </div>

              <div className="relative flex gap-2">
                <div className="relative flex-1 basis-0 min-w-0">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={fraza}
                    onChange={(e) => setFraza(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') szukaj() }}
                    placeholder={t(`hero.szukanie.${(KATEGORIE_SZUKANIA.find((k) => k.key === kategoriaSzukania)
                      || KATEGORIE_SZUKANIA[0]).key}.podpowiedz`)}
                    aria-label={t('hero.samodzielnie.szukaj_aria')}
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  type="button"
                  onClick={szukaj}
                  className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold
                             hover:bg-blue-500 active:bg-blue-700 transition-colors flex-shrink-0"
                >
                  {t('hero.samodzielnie.szukaj')}
                </button>
              </div>
            </div>

            {/* Siatka 2×2, turbiny wracają jako zapowiedź, więc kategorii są cztery.
                Kafelki zostają WYRAŹNIE poziome (ok. 2:1), jak na produkcji: przy
                kwadratowym kadrze zdjęcie panelu czy falownika nie jest czytelne. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {KATEGORIE_PRZEGLADANIA.map((kat) => {
                const tytul = t(`hero.kategorie.${kat.licznik}`)
                const cta = t('hero.kategorie.cta')
                return (
                /* Dwa różne cele na jednym kafelku, więc kafelek NIE jest
                   odnośnikiem: warstwa rozciągnięta na całość prowadzi do
                   listingu, a plakietka „Pomóż wybrać" leży nad nią i prowadzi
                   do asystenta. Odnośnik w odnośniku jest nieprawidłowy i psuje
                   nawigację klawiaturą. */
                <div key={kat.licznik}
                     className={`kb-tile vv-kafel-foto group relative h-32 2xl:h-36 rounded-2xl
                                 overflow-hidden ${kat.to ? '' : 'opacity-70'}`}>
                  <img src={kat.img} alt={tytul}
                       className="kb-img absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                  {/* Liczba modeli z bazy w rogu kafelka: wcześniej stała jako
                      osobny wiersz pod krokami asystenta, z dala od kategorii,
                      której dotyczy. Gdy API nie odpowie, plakietki nie ma. */}
                  {liczby && doSetek(liczby[kat.licznik] || 0) && (
                    <span className="absolute top-3 right-3 z-[5] inline-flex items-baseline gap-1
                                     px-2.5 py-1 rounded-lg bg-white/90 backdrop-blur-sm shadow-sm
                                     text-blue-700 pointer-events-none">
                      <span className="text-sm font-bold leading-none">{doSetek(liczby[kat.licznik])}</span>
                      <span className="text-[10px] font-medium text-gray-600 leading-none">
                        {/* Odmiana według liczby NA PLAKIETCE („300+”), nie surowej. */}
                        {t('hero.kategorie.modeli', { count: parseInt(doSetek(liczby[kat.licznik]), 10) })}
                      </span>
                    </span>
                  )}

                  {kat.to && (
                    <Link to={kat.to} aria-label={`${tytul}: ${cta}`}
                          className="absolute inset-0 z-10 rounded-2xl focus:outline-none
                                     focus-visible:ring-2 focus-visible:ring-white" />
                  )}

                  <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between gap-2">
                    <div className="min-w-0 text-white">
                      <h3 className="text-lg font-bold drop-shadow leading-tight">{tytul}</h3>
                      <span className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-white/90">
                        {cta}
                        {kat.to && <ChevronRightIcon className="w-3.5 h-3.5" />}
                      </span>
                    </div>
                    {/* Asystenci pojedynczych komponentów są tylko po polsku. */}
                    {kat.dobor && jestPolska && (
                      /* Bez ikony: obok „Sprawdź oferty" ze strzałką i tytułu
                         kategorii trzeci znaczek robił z rogu kafelka tłok.
                         Powiększenie pod kursorem zamiast samej zmiany tła —
                         plakietka leży na zdjęciu, więc ruch niesie się lepiej
                         niż odcień. */
                      <Link to={kat.dobor}
                            className="relative z-20 flex-shrink-0 inline-flex items-center
                                       px-3.5 py-2 rounded-lg bg-white/90 text-blue-700
                                       text-[13px] font-bold backdrop-blur-sm shadow-sm
                                       hover:bg-white hover:scale-110 hover:shadow-md
                                       transition-all duration-150 origin-bottom-right">
                        {t('hero.kategorie.pomoz_wybrac')}
                      </Link>
                    )}
                  </div>
                </div>
                )
              })}
            </div>

            {/* Wejście do sprawdzarki kompatybilności. Stoi POD kategoriami, bo to
                narzędzie, a nie kolejna lista produktów, i dlatego wygląda inaczej:
                jasne tło zamiast zdjęcia, żywa animacja zamiast statycznego kadru.
                Rysunek w stylu rysunku oferty (2026-09-30), a nie ten sam co na
                ekranie wyników modułu: tamten pokazuje wynik i ma zostać bez zmian. */}
            <Link
              to="/kompatybilnosc"
              className="group mt-3 flex items-center gap-4 rounded-2xl border border-blue-200
                         bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4
                         shadow-sm hover:shadow-lg hover:border-blue-300 hover:-translate-y-0.5
                         transition-all duration-200"
            >
              <GrafikaKompatybilnosc className="w-28 sm:w-36 flex-shrink-0 -my-1" />
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-gray-900 leading-tight">
                  {t('hero.zgodnosc.tytul')}
                </h3>
                <p className="text-xs text-gray-600 mt-1 leading-5">
                  {t('hero.zgodnosc.opis')}
                </p>
                <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-blue-600">
                  {t('hero.zgodnosc.cta')}
                  <ChevronRightIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>

            {/* Kafelek prowadzi do kreatora doboru wallboxa do auta. Był makietą
                z plakietką „Wkrótce", dopóki funkcja nie istniała — teraz istnieje,
                więc plakietka znika, a <div> staje się odnośnikiem.
                Uczciwość opisu: kreator dobiera ładowarkę DO AUTA. Oś falownika
                (ładowanie z nadwyżek PV) jest w katalogu jako filtr, ale pełnej
                sprawdzarki trójstronnej nadal nie ma — stąd zmiana nagłówka. */}
            {/* Dobór ładowarki działa tylko w Polsce (brak ofert ładowarek w innych krajach). */}
            {jestPolska && (
            <Link
              to="/dobor-ladowarki"
              className="group mt-3 flex items-center gap-4 rounded-2xl border border-blue-200
                         bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 shadow-sm
                         transition-shadow hover:shadow-md"
            >
              <GrafikaFalownikLadowarkaAuto className="w-32 sm:w-44 flex-shrink-0 -my-1" />
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-gray-900 leading-tight">
                  {t('hero.wallbox.tytul')}
                </h3>
                <p className="text-xs text-gray-600 mt-1 leading-5">
                  {t('hero.wallbox.opis')}
                </p>
                <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-blue-700">
                  {t('hero.wallbox.cta')}
                  <ChevronRightIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
            )}
          </aside>

        </div>
      </div>

      <KalkulatorMocy
        otwarty={kalkulatorOtwarty}
        magazynWZestawie={Boolean(wybrane.battery)}
        onZamknij={() => setKalkulatorOtwarty(false)}
        onUzyj={(wynik) => {
          setMoc(String(wynik.mocKwp))
          setPojemnosc(wynik.pojemnoscMagazynuKwh ? String(wynik.pojemnoscMagazynuKwh) : '')
          setKalkulatorOtwarty(false)
        }}
      />
    </section>
  )
}

export default HeroAssistant
